import { motion } from "framer-motion";
import { FileText, Ticket, FolderHeart, ListVideo, Rocket, Radio, ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, Eyebrow } from "./Primitives";

const icons = [FileText, Ticket, FolderHeart, ListVideo, Rocket, Radio];
const ease = [0.16, 1, 0.3, 1];

export const ExperienceEngine = () => {
  const { t } = useLang();
  return (
    <section id="engine" data-testid="engine-section" className="relative overflow-hidden bg-night py-24 text-white lg:py-32">
      <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-violet-700/30 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-pink-600/20 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><Eyebrow className="text-pink-300">{t.engine.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 data-testid="engine-title" className="font-display mt-4 text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">{t.engine.title}</h2>
          </Reveal>
          <Reveal delay={0.16}><p className="mt-5 text-base leading-relaxed text-violet-200/80 sm:text-lg">{t.engine.sub}</p></Reveal>
        </div>

        <div className="mt-16 flex flex-col items-center">
          <Reveal>
            <div className="relative rounded-3xl border border-white/15 bg-white/5 px-10 py-6 text-center backdrop-blur">
              <span className="eyebrow text-violet-300">Source</span>
              <p className="font-display mt-1 text-3xl font-extrabold uppercase tracking-tight">{t.engine.source}</p>
              <span className="absolute -inset-px -z-10 rounded-3xl bg-gradient-to-r from-violet-500/40 to-pink-500/40 blur-xl" />
            </div>
          </Reveal>
          <motion.div initial={{ opacity: 0, scaleY: 0 }} whileInView={{ opacity: 1, scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }} className="my-4 flex origin-top flex-col items-center">
            <span className="h-10 w-px bg-gradient-to-b from-violet-400 to-pink-400" />
            <ArrowDown size={16} className="text-pink-400" />
          </motion.div>
          <div className="grid w-full gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {t.engine.outputs.map((o, i) => {
              const Icon = icons[i];
              return (
                <motion.div
                  key={o.k}
                  data-testid={`engine-node-${o.k.toLowerCase()}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: 0.1 + i * 0.09, ease }}
                  whileHover={{ y: -6 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:border-pink-400/50 hover:bg-white/[0.08]"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-pink-500 text-white"><Icon size={18} /></span>
                  <p className="eyebrow mt-5 text-[0.6rem] text-violet-300">{o.k}</p>
                  <p className="font-display mt-1 text-lg font-bold">{o.t}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-violet-200/70">{o.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export const HowItWorks = () => {
  const { t } = useLang();
  return (
    <section id="how" data-testid="how-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
      <Reveal><Eyebrow>{t.how.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.08}>
        <h2 data-testid="how-title" className="font-display mt-4 max-w-2xl text-3xl font-bold leading-[1.05] tracking-tight text-ink sm:text-4xl lg:text-5xl">{t.how.title}</h2>
      </Reveal>
      <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-5">
        {t.how.steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08} className="bg-white">
            <div className="group h-full p-6 transition-colors hover:bg-lilac lg:p-7">
              <p className="font-display text-5xl font-extrabold tracking-tight text-line transition-colors group-hover:text-gradient">{s.n}</p>
              <p className="font-display mt-8 text-xl font-bold text-ink">{s.t}</p>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
